import type { HTMLAttributes, Ref } from "react";

import { card, density as densityClass } from "~/components/Card.css";

export type CardDensity = "standard" | "dense";

export type CardAccent =
  | "neutral"
  | "primary"
  | "success"
  | "warning"
  | "danger"
  | "info";

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Left-border accent color. Defaults to "neutral" (no accent).
   * The accent pattern comes from Teresa's design cue of "card-based
   * layout with colored left-border accents" — use it to denote
   * status or urgency on dashboard cards.
   */
  accent?: CardAccent;
  /**
   * Padding tier. Defaults to "standard"; "dense" for cards that repeat
   * many times on one screen.
   */
  density?: CardDensity;
  ref?: Ref<HTMLDivElement>;
}

/**
 * Content container. Renders a <div> with surface background, border,
 * and padding — the common "card" pattern. Accepts an optional accent
 * variant that paints a colored left border.
 *
 * Cards do NOT include a title slot or header/body sub-components.
 * Compose with Stack inside the card body for structured content;
 * use the global <h2>/<h3> typography defaults from base.css for
 * headings.
 */
export function Card({
  accent = "neutral",
  density = "standard",
  className,
  ref,
  ...rest
}: CardProps) {
  const merged = [card[accent], densityClass[density], className]
    .filter((c) => c !== undefined && c.length > 0)
    .join(" ");
  return <div ref={ref} className={merged} {...rest} />;
}
